/**
 * <pg-newsletter-popup>: opens the signup dialog (markup: sections/pg-newsletter-popup.liquid).
 *
 * - First visit: `data-trigger="exit"` opens on exit intent (desktop: the pointer leaves through
 *   the top of the window; touch: a fast scroll back up after reading), armed after a few
 *   seconds on the page; `data-trigger="delay"` opens after `data-delay` seconds. Either way it
 *   waits for the cookie banner to be answered and for other dialogs to close.
 * - Closing it snoozes it for `data-snooze-days`; a signup (popup or footer form) hides it
 *   for good on this browser.
 * - After the form posts, Shopify reloads the page on #pg-newsletter-popup: the dialog
 *   reopens at once on the success message or the error.
 * - In the theme editor it opens when its section is selected.
 */
import { PgElement, define } from '@pg/core';
import { openDialog } from '@pg/ui';

const KEY = 'pg-newsletter-popup';
const DAY = 86_400_000;
/** Exit intent is ignored this long after load, so a stray first mouse move doesn't count. */
const ARM_AFTER = 4000;

/** @returns {{ subscribed?: boolean, snoozedUntil?: number }} */
function read() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || '{}');
  } catch {
    return {};
  }
}

/** @param {{ subscribed?: boolean, snoozedUntil?: number }} state */
function write(state) {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {}
}

class PgNewsletterPopup extends PgElement {
  /** @type {number|undefined} */
  #timer;

  mount() {
    const dialog = /** @type {HTMLDialogElement} */ (this.refs.dialog);
    if (!dialog) return;

    if (this.hasAttribute('data-design-mode')) {
      const inSection = (/** @type {Event} */ event) => /** @type {Element} */ (event.target).contains(this);
      document.addEventListener('shopify:section:select', (e) => inSection(e) && openDialog(dialog.id, null), {
        signal: this.signal,
      });
      document.addEventListener('shopify:section:deselect', (e) => inSection(e) && dialog.close(), {
        signal: this.signal,
      });
      return;
    }

    // A signup from either form on this page load (Shopify reloads on the form's id).
    const posted = location.hash === '#pg-newsletter-popup';
    if (
      (posted && dialog.querySelector('[data-pg-newsletter-done]')) ||
      (location.hash === '#pg-footer-newsletter' && document.querySelector('.pg-footer__form-success'))
    ) {
      write({ subscribed: true });
    }

    dialog.addEventListener(
      'close',
      () => {
        if (!read().subscribed) write({ snoozedUntil: Date.now() + Number(this.dataset.snoozeDays || 30) * DAY });
      },
      { signal: this.signal },
    );

    if (posted) {
      const url = new URL(location.href);
      url.searchParams.delete('customer_posted');
      url.hash = '';
      history.replaceState(history.state, '', url);
      this.#open(dialog);
      if (dialog.querySelector('[data-pg-newsletter-error]')) dialog.querySelector('input[type="email"]')?.focus();
      return;
    }

    const state = read();
    if (state.subscribed || (state.snoozedUntil && state.snoozedUntil > Date.now())) return;
    if (this.dataset.trigger === 'delay') {
      this.#timer = window.setTimeout(() => this.#open(dialog), Number(this.dataset.delay || 15) * 1000);
    } else {
      this.#timer = window.setTimeout(() => this.#watchExit(dialog), ARM_AFTER);
    }
  }

  /** @param {HTMLDialogElement} dialog */
  #watchExit(dialog) {
    const watch = new AbortController();
    const signal = AbortSignal.any([this.signal, watch.signal]);
    const fire = () => {
      watch.abort();
      this.#open(dialog);
    };

    // Desktop: the pointer leaves the page through the top edge (tabs, address bar, close).
    document.documentElement.addEventListener(
      'mouseleave',
      (event) => {
        if (event.clientY <= 0) fire();
      },
      { signal },
    );

    // Touch: after reading at least a screen, a quick flick back up (heading for the address bar).
    if (matchMedia('(hover: none)').matches) {
      let lastY = scrollY;
      let lastT = performance.now();
      let deepest = scrollY;
      addEventListener(
        'scroll',
        () => {
          const y = scrollY;
          const t = performance.now();
          deepest = Math.max(deepest, y);
          const speed = (lastY - y) / Math.max(t - lastT, 1); // px/ms, positive = upwards
          if (deepest > innerHeight && lastY - y > 60 && speed > 1.5) fire();
          lastY = y;
          lastT = t;
        },
        { passive: true, signal },
      );
    }
  }

  unmount() {
    clearTimeout(this.#timer);
  }

  /** @param {HTMLDialogElement} dialog */
  #open(dialog) {
    // Let the cookie banner be answered first.
    if (document.getElementById('shopify-pc__banner')?.checkVisibility()) {
      this.#timer = window.setTimeout(() => this.#open(dialog), 3000);
      return;
    }
    // Don't stack over the cart drawer, search or quick add: wait until they close.
    if (document.querySelector('dialog[open]')) {
      document.addEventListener('close', () => this.#open(dialog), { capture: true, once: true, signal: this.signal });
      return;
    }
    openDialog(dialog.id, null);
  }
}

define('pg-newsletter-popup', PgNewsletterPopup);
